/**
 * Used in chat widgets (the loaders and the iframe) to handle co-op coverage hours for checking statuses/fallbacks.
 */
import { isCoverageInRange } from '../../tools/coverageChecker.js';

/**
 * Strip out the coverage data so we aren't passing that through the request.
 * It is not being used server side in any way and just makes the request bigger.
 * @param {Object} rule Status rule.
 * @returns {Object}
 */
const removeCoverageFromRule = function(rule) {
    return {
        u: rule.u,
        d: rule.d,
        c: rule.c,
        fallbackSeconds: rule.fallbackSeconds || 0
    };
};

/**
 * Adjust the default status rules to account for Co-Op coverage settings.
 * @param {Object[]} onlineRules Widget's status rules.
 * @returns {Object[]}
 */
const adjustStatusRequestForCoverage = function(onlineRules) {
    const finalRules = [];
    const now = new Date();
    const day = now.getUTCDay();
    const hour = now.getUTCHours();
    const minute = now.getUTCMinutes();

    onlineRules.forEach((rule) => {
        // Keep all non-Coop rules.
        // And 24/7 coverage.
        if (!rule.c || rule.coverageType === 1) {
            finalRules.push(removeCoverageFromRule(rule));
            return;
        }
        // no coverage
        if (rule.coverageType === 0) {
            return;
        }
        const times = rule.coverageHours;
        if (!times || !times[day] || times[day].length === 0) {
            return;
        }
        const ranges = times[day];
        if (!isCoverageInRange(hour, minute, ranges)) {
            return;
        }
        finalRules.push(removeCoverageFromRule(rule));
    });
    return finalRules;
};

/**
 * Fallback is Coop only so filter out the rest and ones that have 0 seconds fallback
 * @param {Array<Object>} onlineRules op/dept/coop status rules
 * @param {Number} defaultTime Widget default fallback setting
 * @returns {Array<Object>}
 */
const adjustFallbacksForCoverage = function(onlineRules, defaultTime) {
    const adjustedRules = adjustStatusRequestForCoverage(onlineRules);
    return adjustedRules.filter((rule) => {
        if (!rule.c) {
            return false;
        }
        rule.fallbackSeconds = Number(rule.fallbackSeconds || defaultTime);
        return rule.fallbackSeconds > 0;
    });
};

export {
    adjustStatusRequestForCoverage,
    adjustFallbacksForCoverage
};
